import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '../../Components/Header/Header';
import Sidebar from '../../Components/Sidebar/Sidebar';
import ConfirmationModal from '../../Components/Shared/ConfirmationModal';
import LeaveReviewModal from '../../Components/Bookings/LeaveReviewModal';
import {
  getBookingDetails,
  getBookingTimeline,
  cancelBooking,
  startBooking,
  completeBooking,
  confirmCompletion,
  raiseDispute,
  respondToDispute
} from '../../api/bookings';
import { uploadOfferingImage } from '../../api/offerings';
import { createCheckoutSession } from '../../api/payments';
import '../AdventureShared.css';
import './CustomerOrderDetailPage.css';
import CustomDropdown from '../../Components/Shared/CustomDropdown';

const DISPUTE_REASON_OPTIONS = [
  { value: 'service_not_completed', label: 'Service Not Completed' },
  { value: 'wrong_service', label: 'Wrong Service' },
  { value: 'quality_issue', label: 'Quality Issue' },
  { value: 'no_show', label: 'No Show' },
  { value: 'other', label: 'Other' }
];

export default function CustomerOrderDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [booking, setBooking] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [completeModalOpen, setCompleteModalOpen] = useState(false);
  const [disputeModalOpen, setDisputeModalOpen] = useState(false);

  const [cancelReason, setCancelReason] = useState('');
  const [completionNotes, setCompletionNotes] = useState('');
  const [disputeReason, setDisputeReason] = useState('service_not_completed');
  const [disputeDescription, setDisputeDescription] = useState('');
  const [evidenceUrls, setEvidenceUrls] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);
  const [isSubmittingDispute, setIsSubmittingDispute] = useState(false);
  const [showStartConfirm, setShowStartConfirm] = useState(false);
  const [showConfirmCompletionModal, setShowConfirmCompletionModal] = useState(false);

  const [providerDisputeModalOpen, setProviderDisputeModalOpen] = useState(false);
  const [providerExplanationText, setProviderExplanationText] = useState('');
  const [providerEvidenceUrls, setProviderEvidenceUrls] = useState([]);
  const [isSubmittingProviderDispute, setIsSubmittingProviderDispute] = useState(false);
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);

  const currentUserId = localStorage.getItem("userId") || "";

  const fetchDetailsAndTimeline = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const [bookingData, timelineData] = await Promise.all([
        getBookingDetails(id),
        getBookingTimeline(id).catch(e => { console.warn(e); return { timeline: [] }; })
      ]);
      setBooking(bookingData);
      if (timelineData && Array.isArray(timelineData.timeline)) {
        setTimeline(timelineData.timeline);
      } else {
        setTimeline(bookingData.statusHistory || []);
      }
    } catch (err) {
      console.error("Failed to load order details:", err);
      setError("Failed to load order details. Please verify your connection.");
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchDetailsAndTimeline(); }, [id, fetchDetailsAndTimeline]);

  const handleProceedToPayment = async () => {
    setIsRedirecting(true);
    setError('');
    try {
      const successUrl = `${window.location.origin}/payment/success?bookingId=${id}`;
      const cancelUrl = `${window.location.origin}/payment/cancel?bookingId=${id}`;

      const res = await createCheckoutSession(id, successUrl, cancelUrl);
      if (res && res.data) {
        window.location.href = res.data;
      } else {
        throw new Error("Invalid response from checkout session API.");
      }
    } catch (err) {
      console.error("Failed to proceed to payment:", err);
      const msg = err.response?.data?.message || err.message;
      setError("Failed to redirect to payment: " + msg);
      setIsRedirecting(false);
    }
  };

  /* ── Loading & Error shells ── */
  const Shell = ({ children }) => (
    <div className="CustomerOrderDetailPage">
      <div className="adventurePageContainer">
        <div className="headerContain"><Header /></div>
        <div className="adventurePageContainer2">
          <div className="sidebarContain"><Sidebar /></div>
          <main className="adv-page-main">{children}</main>
        </div>
      </div>
    </div>
  );

  if (isLoading) return (
    <Shell>
      <div className="adv-detail-spinner-container">
        <div className="adv-detail-spinner"></div>
        <div className="adv-state-text" style={{ padding: 0 }}>Loading order details...</div>
      </div>
    </Shell>
  );

  if (error || !booking) return (
    <Shell>
      <div className="adv-state-error" style={{ paddingTop: '80px', textAlign: 'center' }}>
        <h3>{error || "Order not found!"}</h3>
        <button className="adv-btn-primary" style={{ marginTop: '16px' }} onClick={() => navigate('/facilitate/orders')}>
          Go Back to Orders
        </button>
      </div>
    </Shell>
  );

  const isCustomer = (booking.customerId === currentUserId || booking.customer?.userId === currentUserId);
  const isProvider = (booking.providerId === currentUserId || booking.provider?.userId === currentUserId);

  const formatPriceVal = (amount, model, currency) => {
    if (model === 'free') return 'Free';
    if (model === 'cod') return 'Cash on Delivery';

    let cleanCurrency = (currency || 'USD').trim().toUpperCase();
    if (cleanCurrency === 'RS') cleanCurrency = 'INR';
    if (!/^[A-Z]{3}$/.test(cleanCurrency)) cleanCurrency = 'USD';

    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: cleanCurrency }).format(amount);
    } catch (e) {
      return `${cleanCurrency} ${(amount || 0).toFixed(2)}`;
    }
  };

  const handleCancelSubmit = async (e) => {
    e.preventDefault();
    if (!cancelReason.trim()) return alert('Please enter a cancellation reason.');
    setIsCancelling(true);
    try {
      await cancelBooking(booking.bookingId, cancelReason);
      setCancelModalOpen(false); setCancelReason('');
      fetchDetailsAndTimeline();
    } catch (err) { console.error(err); alert("Failed to cancel order."); }
    finally { setIsCancelling(false); }
  };

  const handleStartServiceAction = () => {
    setShowStartConfirm(true);
  };

  const handleConfirmStartService = async () => {
    setIsStarting(true);
    try { await startBooking(booking.bookingId); fetchDetailsAndTimeline(); }
    catch (err) { console.error(err); alert("Failed to mark service as started."); }
    finally { setIsStarting(false); setShowStartConfirm(false); }
  };

  const handleCompleteSubmit = async (e) => {
    e.preventDefault();
    setIsCompleting(true);
    try {
      await completeBooking(booking.bookingId, completionNotes);
      setCompleteModalOpen(false); setCompletionNotes('');
      fetchDetailsAndTimeline();
    } catch (err) { console.error(err); alert("Failed to mark service as completed."); }
    finally { setIsCompleting(false); }
  };

  const handleConfirmCompletionAction = () => {
    setShowConfirmCompletionModal(true);
  };

  const handleConfirmConfirmCompletion = async () => {
    setIsConfirming(true);
    try { await confirmCompletion(booking.bookingId); fetchDetailsAndTimeline(); }
    catch (err) { console.error(err); alert("Failed to confirm completion."); }
    finally { setIsConfirming(false); setShowConfirmCompletionModal(false); }
  };

  const handleEvidenceFileChange = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    setUploading(true);
    for (const file of files) {
      try {
        const res = await uploadOfferingImage(file);
        if (res?.url) setEvidenceUrls(prev => [...prev, res.url]);
      } catch (err) {
        console.error("Evidence upload failed for file: " + file.name, err);
        alert(`Failed to upload ${file.name}. Please try again.`);
      }
    }
    setUploading(false);
  };

  const handleDisputeSubmit = async (e) => {
    e.preventDefault();
    if (!disputeDescription.trim()) return alert("Please explain the reason for the dispute.");
    setIsSubmittingDispute(true);
    try {
      await raiseDispute(booking.bookingId, { reason: disputeReason, description: disputeDescription, evidenceUrls });
      setDisputeModalOpen(false); setDisputeDescription(''); setEvidenceUrls([]);
      fetchDetailsAndTimeline();
    } catch (err) { console.error(err); alert("Failed to raise dispute."); }
    finally { setIsSubmittingDispute(false); }
  };

  const handleProviderEvidenceFileChange = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    setUploading(true);
    for (const file of files) {
      try {
        const res = await uploadOfferingImage(file);
        if (res?.url) setProviderEvidenceUrls(prev => [...prev, res.url]);
      } catch (err) {
        console.error("Provider evidence upload failed for file: " + file.name, err);
        alert(`Failed to upload ${file.name}. Please try again.`);
      }
    }
    setUploading(false);
  };

  const handleProviderDisputeSubmit = async (e) => {
    e.preventDefault();
    if (!providerExplanationText.trim()) return alert("Please explain your response to the dispute.");
    setIsSubmittingProviderDispute(true);
    try {
      await respondToDispute(booking.bookingId, providerExplanationText, providerEvidenceUrls);
      setProviderDisputeModalOpen(false);
      setProviderExplanationText('');
      setProviderEvidenceUrls([]);
      fetchDetailsAndTimeline();
    } catch (err) {
      console.error(err);
      alert("Failed to submit dispute response.");
    } finally {
      setIsSubmittingProviderDispute(false);
    }
  };

  const statusColor = (s) => {
    const map = { confirmed: '#48d1cc', pending: '#f5c842', in_progress: '#ffd700', completed: '#28a745', closed: '#20b2aa', disputed: '#ff4500', cancelled: '#dc3545', paid: '#28a745' };
    return map[s] || '#dc3545';
  };

  return (
    <div className="CustomerOrderDetailPage">
      <div className="adventurePageContainer">
        <div className="headerContain"><Header /></div>

        <div className="adventurePageContainer2">
          <div className="sidebarContain"><Sidebar /></div>

          <main className="adv-page-main">
            {/* Page Header */}
            <div className="adv-detail-page-header">
              <div>
                <h1 className="adv-page-title">Order <span className="adv-page-title-accent">Details</span></h1>
              </div>
              <button className="adv-btn-outline" onClick={() => isProvider ? navigate('/facilitate/orders/inbox') : navigate('/facilitate/orders')}>
                ← Back
              </button>
            </div>

            <div className="adv-detail-grid">
              {/* ── Left Panel ── */}
              <div className="adv-detail-left">

                {/* Cover Card */}
                <div className="adv-card">
                  <img
                    src={booking.coverImageUrl || "https://images.unsplash.com/photo-1531403009284-440f080d1e12?auto=format&fit=crop&w=800&q=80"}
                    alt={booking.offeringTitle}
                    className="adv-detail-cover"
                  />
                  <h2 className="adv-detail-title">{booking.offeringTitle}</h2>
                  <div className="adv-detail-meta-row">
                    <span className="adv-status" style={{ color: statusColor(booking.status), backgroundColor: `${statusColor(booking.status)}18` }}>
                      {booking.status}
                    </span>
                    <span className="adv-detail-date-note">Requested on {new Date(booking.createdAt).toLocaleDateString()}</span>
                  </div>
                </div>

                {/* Counterpart info */}
                {isCustomer && (
                  <div
                    className="adv-card adv-card--row clickable-provider-card"
                    onClick={() => navigate(`/facilitate/providers/${booking.providerId}`)}
                    style={{ cursor: 'pointer' }}
                  >
                    <img src={booking.providerAvatarUrl || "/profilePic.jpg"} alt={booking.providerName} className="adv-card-avatar" />
                    <div>
                      <p className="adv-detail-label">Service Provider</p>
                      <p className="adv-detail-link">{booking.providerName} →</p>
                    </div>
                  </div>
                )}
                {isProvider && (
                  <div className="adv-card adv-card--row">
                    <img src={booking.customerAvatarUrl || "/profilePic.jpg"} alt={booking.customerName} className="adv-card-avatar" />
                    <div>
                      <p className="adv-detail-label">Client / Customer</p>
                      <p className="adv-detail-link">{booking.customerName}</p>
                    </div>
                  </div>
                )}

                {/* Shipping / Service Address */}
                {booking.shippingAddressLine && (
                  <div className="adv-card adv-card--col">
                    <div className="adv-note-block animate-fade-in" style={{ borderLeftColor: '#ffb703', background: 'rgba(255, 183, 3, 0.02)' }}>
                      <p className="adv-detail-label" style={{ color: '#ffb703' }}>
                        🏡 {booking.category === 'product' ? 'Shipping Details' : 'Service Address'}
                      </p>
                      <p className="adv-note-text" style={{ fontSize: '14.5px', fontWeight: '500' }}>
                        {booking.shippingAddressLine}
                        <br />
                        {booking.shippingCity}, {booking.shippingState} {booking.shippingZipcode}
                        <br />
                        {booking.shippingCountry}
                      </p>
                    </div>
                  </div>
                )}

                {/* Notes */}
                <div className="adv-card adv-card--col">
                  <div className="adv-note-block">
                    <p className="adv-detail-label">Customer Notes</p>
                    <p className="adv-note-text">{booking.customerNotes || 'No notes left for the provider.'}</p>
                  </div>

                  {booking.providerNotes && (
                    <div className="adv-note-block adv-note-block--teal">
                      <p className="adv-detail-label">Provider Confirmation Message</p>
                      <p className="adv-note-text">{booking.providerNotes}</p>
                    </div>
                  )}

                  {booking.completionNotes && (
                    <div className="adv-note-block adv-note-block--green">
                      <p className="adv-detail-label">Provider Completion Notes</p>
                      <p className="adv-note-text">{booking.completionNotes}</p>
                    </div>
                  )}

                  {booking.status === 'cancelled' && booking.cancelReason && (
                    <div className="adv-note-block adv-note-block--red">
                      <p className="adv-detail-label">Cancellation Reason</p>
                      <p className="adv-note-text">{booking.cancelReason}</p>
                    </div>
                  )}

                  {booking.status === 'cancelled' && booking.declineReason && (
                    <div className="adv-note-block adv-note-block--red">
                      <p className="adv-detail-label">Decline Reason</p>
                      <p className="adv-note-text"><strong>{booking.declineReason}</strong></p>
                    </div>
                  )}

                  {booking.hasReview && booking.review && (
                    <div className="adv-note-block adv-note-block--green" style={{ borderLeftColor: '#ffd700', background: 'rgba(255,215,0,0.02)' }}>
                      <p className="adv-detail-label" style={{ color: '#ffd700', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span>⭐</span> Your Review ({booking.review.rating} / 5 Stars)
                      </p>
                      <p className="adv-note-text" style={{ fontStyle: 'italic', fontSize: '14.5px' }}>
                        "{booking.review.comment}"
                      </p>
                      {booking.review.photoUrls?.length > 0 && (
                        <div className="adv-evidence-grid" style={{ marginTop: '10px' }}>
                          {booking.review.photoUrls.map((url, i) => (
                            <img key={i} src={url} alt="Review attachment" className="adv-evidence-img" />
                          ))}
                        </div>
                      )}

                      {booking.review.providerResponse && (
                        <div style={{ marginTop: '14px', borderTop: '1px solid rgba(255,215,0,0.15)', paddingTop: '10px' }}>
                          <p className="adv-detail-label" style={{ color: '#5AAF9A', fontSize: '13px', marginBottom: '4px' }}>Provider Response</p>
                          <p className="adv-note-text" style={{ fontSize: '13.5px' }}>
                            {booking.review.providerResponse}
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {booking.dispute && (
                    <div className={`adv-note-block ${booking.dispute.status === 'resolved' ? 'adv-note-block--teal' : 'adv-note-block--yellow'}`}>
                      {booking.dispute.status === 'resolved' ? (
                        <div style={{ marginBottom: '16px', paddingBottom: '12px', borderBottom: '1px solid rgba(72,209,204,0.2)' }}>
                          <p className="adv-detail-label" style={{ color: '#48d1cc', fontSize: '15px', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span>⚖️</span> Dispute Resolved
                          </p>
                          <p className="adv-note-text" style={{ fontSize: '14px', marginTop: '6px' }}>
                            <strong>Outcome:</strong> {booking.paymentStatus === 'refunded' ? (
                              <span style={{ color: '#ff6b6b', fontWeight: '600' }}>Resolved in favor of Customer (Full refund issued)</span>
                            ) : (
                              <span style={{ color: '#28a745', fontWeight: '600' }}>Resolved in favor of Provider (Payout released)</span>
                            )}
                          </p>
                          {booking.dispute.resolvedAt && (
                            <p style={{ margin: '6px 0 0 0', fontSize: '11px', opacity: 0.6 }}>
                              Resolved on {new Date(booking.dispute.resolvedAt).toLocaleString()}
                            </p>
                          )}
                        </div>
                      ) : (
                        <p className="adv-detail-label">Dispute Investigation Details</p>
                      )}
                      
                      <p className="adv-note-text"><strong>Reason:</strong> {booking.dispute.reason}</p>
                      <p className="adv-note-text"><strong>Description:</strong> {booking.dispute.description}</p>
                      {booking.dispute.evidenceUrls?.length > 0 && (
                        <div className="adv-evidence-grid">
                          {booking.dispute.evidenceUrls.map((url, i) => (
                            <img key={i} src={url} alt="Dispute evidence" className="adv-evidence-img" />
                          ))}
                        </div>
                      )}

                      {booking.dispute.providerExplanation ? (
                        <div style={{ marginTop: '16px', borderTop: booking.dispute.status === 'resolved' ? '1px solid rgba(72,209,204,0.15)' : '1px solid rgba(255,215,0,0.15)', paddingTop: '12px' }}>
                          <p className="adv-detail-label" style={{ color: booking.dispute.status === 'resolved' ? '#48d1cc' : '#ffd700', fontSize: '13.5px', marginBottom: '6px' }}>Provider Response & Counter-Evidence</p>
                          <p className="adv-note-text"><strong>Explanation:</strong> {booking.dispute.providerExplanation}</p>
                          {booking.dispute.providerEvidenceUrls?.length > 0 && (
                            <div className="adv-evidence-grid" style={{ marginTop: '8px' }}>
                              {booking.dispute.providerEvidenceUrls.map((url, i) => (
                                <img key={i} src={url} alt="Provider dispute evidence" className="adv-evidence-img" />
                              ))}
                            </div>
                          )}
                          {booking.dispute.providerRespondedAt && (
                            <p style={{ margin: '8px 0 0 0', fontSize: '11px', opacity: 0.6 }}>
                              Responded on {new Date(booking.dispute.providerRespondedAt).toLocaleString()}
                            </p>
                          )}
                        </div>
                      ) : (
                        booking.dispute.status !== 'resolved' && (
                          <div style={{ marginTop: '14px', borderTop: '1px solid rgba(255,185,56,0.2)', paddingTop: '10px' }}>
                            <p className="adv-note-text" style={{ fontStyle: 'italic', opacity: 0.7 }}>
                              ⏳ Waiting for provider to upload counter-explanation or counter-evidence.
                            </p>
                          </div>
                        )
                      )}
                    </div>
                  )}
                </div>

                {/* Order Tracking (Phase 6) */}
                {booking.fulfillments && booking.fulfillments.length > 0 && (
                  <div className="adv-card adv-card--col animate-fade-in" style={{ marginTop: '20px' }}>
                    <h3 className="adv-card-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span>📦</span> Order & Delivery Tracking
                    </h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', width: '100%' }}>
                      {booking.fulfillments.map((ful) => (
                        <div
                          key={ful.fulfillmentId || ful.id}
                          style={{
                            background: 'rgba(255,255,255,0.02)',
                            border: '1px solid rgba(255,255,255,0.06)',
                            borderRadius: '10px',
                            padding: '16px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '12px'
                          }}
                        >
                          <div style={{ display: 'flex', gap: '12px' }}>
                            <div style={{ width: '50px', height: '50px', borderRadius: '6px', overflow: 'hidden', background: 'rgba(0,0,0,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                              {ful.product?.imageUrl ? (
                                <img src={ful.product.imageUrl} alt={ful.product.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                              ) : (
                                <span style={{ fontSize: '20px', opacity: 0.3, margin: 'auto' }}>📦</span>
                              )}
                            </div>
                            <div style={{ overflow: 'hidden' }}>
                              <h4 style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: '#ffffff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{ful.product?.name}</h4>
                              <p style={{ margin: '2px 0 0 0', fontSize: '12px', color: 'rgba(255,255,255,0.5)' }}>Qty: {ful.quantity} · Method: {ful.product?.deliveryMethod === 'pre_ship' ? 'Pre-shipped to address' : ful.product?.deliveryMethod === 'at_event' ? 'Provided at service' : 'Digital download'}</p>
                            </div>
                            <span
                              className={`adv-status adv-status--${ful.status}`}
                              style={{ marginLeft: 'auto', alignSelf: 'flex-start', fontSize: '11px', textTransform: 'capitalize', padding: '3px 8px', borderRadius: '12px' }}
                            >
                              {ful.status}
                            </span>
                          </div>

                          {(ful.status === 'dispatched' || ful.status === 'delivered') && (
                            <div style={{ padding: '10px', background: 'rgba(72,209,204,0.05)', borderRadius: '6px', display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '12px' }}>
                              {ful.carrier && <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ opacity: 0.6 }}>Carrier:</span><strong>{ful.carrier}</strong></div>}
                              {ful.trackingNumber && (
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                  <span style={{ opacity: 0.6 }}>Tracking Number:</span>
                                  <strong>
                                    <a
                                      href={`https://google.com/search?q=${encodeURIComponent(ful.carrier + ' tracking ' + ful.trackingNumber)}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      style={{ color: '#48d1cc', textDecoration: 'underline' }}
                                    >
                                      {ful.trackingNumber}
                                    </a>
                                  </strong>
                                </div>
                              )}
                              {ful.notes && <div style={{ marginTop: '4px', borderTop: '1px solid rgba(255,255,255,0.05)', paddingTop: '4px', fontStyle: 'italic', opacity: 0.8 }}>Note: {ful.notes}</div>}
                            </div>
                          )}

                          {ful.status === 'pending' && ful.product?.deliveryMethod === 'pre_ship' && (
                            <p style={{ margin: 0, fontSize: '11px', color: 'rgba(255,255,255,0.35)', fontStyle: 'italic' }}>Your item is being prepared for dispatch by the provider.</p>
                          )}

                          {ful.status === 'pending' && ful.product?.deliveryMethod === 'digital' && (
                            <p style={{ margin: 0, fontSize: '11px', color: 'rgba(255,255,255,0.35)', fontStyle: 'italic' }}>The download links will become active as soon as the item is dispatched.</p>
                          )}

                          {ful.status === 'delivered' && ful.product?.deliveryMethod === 'digital' && ful.product?.digitalFileUrl && (
                            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '4px' }}>
                              <a
                                href={ful.product.digitalFileUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="adv-btn-primary"
                                style={{ fontSize: '12px', padding: '6px 14px', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                              >
                                📥 Download Digital File
                              </a>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* ── Right Panel ── */}
              <div className="adv-detail-right">

                {/* Schedule & Price */}
                <div className="adv-card">
                  <h3 className="adv-card-title">
                    {booking.category === 'product' ? 'Order Summary' : 'Schedule Summary'}
                  </h3>
                  <div className="adv-schedule-rows">
                    {booking.category === 'product' ? (
                      <>
                        <div className="adv-schedule-row">
                          <span className="adv-detail-label">Order Date:</span>
                          <strong className="adv-detail-value">{new Date(booking.createdAt).toLocaleDateString()}</strong>
                        </div>
                        <div className="adv-schedule-row">
                          <span className="adv-detail-label">Quantity:</span>
                          <strong className="adv-detail-value">{booking.numberOfPeople} {booking.numberOfPeople === 1 ? 'unit' : 'units'}</strong>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="adv-schedule-row"><span className="adv-detail-label">Date:</span><strong className="adv-detail-value">{booking.date}</strong></div>
                        <div className="adv-schedule-row"><span className="adv-detail-label">Time:</span><strong className="adv-detail-value">{booking.time}</strong></div>
                        {booking.durationHours && (
                          <div className="adv-schedule-row">
                            <span className="adv-detail-label">Duration:</span>
                            <strong className="adv-detail-value">{booking.durationHours} {booking.pricingModel === 'per_hour' ? 'hours' : 'days'}</strong>
                          </div>
                        )}
                        {booking.numberOfPeople && (
                          <div className="adv-schedule-row">
                            <span className="adv-detail-label">Guests:</span>
                            <strong className="adv-detail-value">{booking.numberOfPeople} {booking.numberOfPeople === 1 ? 'person' : 'people'}</strong>
                          </div>
                        )}
                      </>
                    )}
                    <div className="adv-schedule-row">
                      <span className="adv-detail-label">Payment Status:</span>
                      <strong
                        className="adv-detail-value"
                        style={{
                          color: booking.paymentStatus === 'paid' ? '#28a745' : '#ffc107',
                          textTransform: 'uppercase'
                        }}
                      >
                        {booking.paymentStatus}
                      </strong>
                    </div>
                    <div className="adv-schedule-divider"></div>
                    <div className="adv-schedule-row">
                      <span className="adv-detail-label">Total Cost:</span>
                      <strong className="adv-schedule-price">{formatPriceVal(booking.calculatedTotal, booking.pricingModel, booking.currency)}</strong>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="adv-action-btns">
                    {isCustomer && (
                      <>
                        {booking.status === 'confirmed' && booking.paymentStatus === 'unpaid' && (
                          <button
                            className="adv-btn-primary adv-btn--full animate-fade-in"
                            onClick={handleProceedToPayment}
                            disabled={isRedirecting}
                            style={{
                              background: '#3bb3ae',
                              borderColor: '#3bb3ae',
                              color: '#071414',
                              fontWeight: '700',
                              marginBottom: '10px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '8px'
                            }}
                          >
                            {isRedirecting ? (
                              <>
                                <span className="adv-btn-spinner"></span>
                                Redirecting...
                              </>
                            ) : (
                              "💳 Pay Now to Secure Order"
                            )}
                          </button>
                        )}
                        {(booking.status === 'pending' || (booking.status === 'confirmed' && booking.paymentStatus !== 'paid')) && (
                          <button className="adv-btn-danger-outline adv-btn--full" onClick={() => setCancelModalOpen(true)}>
                            Cancel Order Request
                          </button>
                        )}
                        {booking.status === 'completed' && (
                          <>
                            <button
                              className="adv-btn-success adv-btn--full"
                              onClick={handleConfirmCompletionAction}
                              disabled={isConfirming}
                              style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}
                            >
                              {isConfirming ? (
                                <>
                                  <span className="adv-btn-spinner"></span>
                                  Confirming...
                                </>
                              ) : (
                                "✓ Yes, Confirm Completion"
                              )}
                            </button>
                            <button className="adv-btn-destructive adv-btn--full" onClick={() => { setDisputeReason('service_not_completed'); setDisputeDescription(''); setEvidenceUrls([]); setDisputeModalOpen(true); }}>⚠ Raise a Dispute</button>
                            <p className="adv-auto-release-note">Notice: Service releases automatically after 24 hours.</p>
                          </>
                        )}
                        {booking.status === 'closed' && (
                          booking.hasReview ? (
                            <div className="adv-waiting-note" style={{ color: '#28a745', border: '1px solid rgba(40,167,69,0.3)', background: 'rgba(40,167,69,0.02)' }}>
                              ✓ Review submitted successfully.
                            </div>
                          ) : (
                            <button
                              className="adv-btn-primary adv-btn--full"
                              onClick={() => setReviewModalOpen(true)}
                              style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center', background: '#00f2fe', borderColor: '#00f2fe', color: '#0c0f13' }}
                            >
                              ✍ Leave a Review
                            </button>
                          )
                        )}
                      </>
                    )}

                    {isProvider && (
                      <>
                        {booking.status === 'confirmed' && (
                          <>
                            <button
                              className="adv-btn-primary adv-btn--full"
                              onClick={handleStartServiceAction}
                              disabled={isStarting || booking.paymentStatus !== 'paid'}
                              style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center', opacity: booking.paymentStatus !== 'paid' ? 0.6 : 1 }}
                            >
                              {isStarting ? (
                                <>
                                  <span className="adv-btn-spinner"></span>
                                  {booking.category === 'product' ? "Fulfilling..." : "Starting..."}
                                </>
                              ) : booking.paymentStatus !== 'paid' ? (
                                booking.category === 'product' ? "▶ Fulfill Order (Awaiting Payment)" : "▶ Start Service (Awaiting Payment)"
                              ) : (
                                booking.category === 'product' ? "▶ Fulfill Order" : "▶ Start Service"
                              )}
                            </button>
                            {booking.paymentStatus !== 'paid' && (
                              <p style={{ color: '#ffc107', fontSize: '12px', marginTop: '6px', textAlign: 'center', fontWeight: '500' }}>
                                {booking.category === 'product'
                                  ? "⚠️ Customer has not paid yet. You can fulfill the order once payment is secure."
                                  : "⚠️ Customer has not paid yet. You can start the service once payment is secure."}
                              </p>
                            )}
                            <button className="adv-btn-danger-outline adv-btn--full" style={{ marginTop: '10px' }} onClick={() => setCancelModalOpen(true)}>Cancel Order</button>
                          </>
                        )}
                        {booking.status === 'in_progress' && (
                          <button className="adv-btn-success adv-btn--full" onClick={() => { setCompletionNotes(''); setCompleteModalOpen(true); }}>
                            {booking.category === 'product' ? '■ Complete Fulfillment' : '■ Complete Service'}
                          </button>
                        )}
                        {booking.status === 'completed' && (
                          <div className="adv-waiting-note">Waiting for client confirmation...</div>
                        )}
                        {booking.status === 'disputed' && (
                          booking.dispute?.providerExplanation ? (
                            <div className="adv-waiting-note" style={{ color: '#ffd700', border: '1px solid rgba(255,185,56,0.3)', background: 'rgba(255,185,56,0.02)' }}>
                              ✓ Dispute response submitted.
                            </div>
                          ) : (
                            <button
                              className="adv-btn-primary adv-btn--full"
                              onClick={() => {
                                setProviderExplanationText('');
                                setProviderEvidenceUrls([]);
                                setProviderDisputeModalOpen(true);
                              }}
                              style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center', background: '#ff7b00', borderColor: '#ff7b00' }}
                            >
                              ✍ Respond to Dispute
                            </button>
                          )
                        )}
                      </>
                    )}
                  </div>
                </div>

                {/* Timeline */}
                <div className="adv-card">
                  <h3 className="adv-card-title">Status Timeline</h3>
                  <div className="adv-timeline">
                    <div className="adv-timeline__track"></div>
                    {timeline && timeline.map((history, idx) => (
                      <div key={idx} className="adv-timeline__item">
                        <div className="adv-timeline__dot" style={{ backgroundColor: statusColor(history.status) }}></div>
                        <div className="adv-timeline__content">
                          <div className="adv-timeline__row">
                            <span className="adv-timeline__status">{history.status}</span>
                            {history.actor && <span className="adv-timeline__actor">By {history.actor}</span>}
                          </div>
                          <span className="adv-timeline__time">{new Date(history.timestamp).toLocaleString()}</span>
                          {history.note && <span className="adv-timeline__note">{history.note}</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>

      {/* Cancel Modal */}
      {cancelModalOpen && (
        <div className="adv-modal-overlay">
          <div className="adv-modal">
            <h2 className="adv-modal__title">Cancel this order?</h2>
            <form onSubmit={handleCancelSubmit}>
              <div className="adv-modal__field">
                <label className="adv-modal__label">Reason for cancellation *</label>
                <textarea required rows="4" placeholder="Explain why you are cancelling..." value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} className="adv-modal__textarea" />
              </div>
              <div className="adv-modal__actions">
                <button
                  type="button"
                  className="adv-btn-ghost"
                  onClick={() => { setCancelModalOpen(false); setCancelReason(''); }}
                  disabled={isCancelling}
                >
                  Go Back
                </button>
                <button
                  type="submit"
                  className="adv-btn-destructive"
                  disabled={isCancelling}
                  style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}
                >
                  {isCancelling ? (
                    <>
                      <span className="adv-btn-spinner"></span>
                      Cancelling...
                    </>
                  ) : (
                    "Yes, Cancel"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Complete Modal */}
      {completeModalOpen && (
        <div className="adv-modal-overlay">
          <div className="adv-modal">
            <h2 className="adv-modal__title">
              {booking.category === 'product' ? 'Mark Order as Fulfilled' : 'Mark Service as Completed'}
            </h2>
            <p className="adv-modal__desc">
              {booking.category === 'product'
                ? 'Confirm shipment or delivery of this product. Leave any tracking details or completion notes below.'
                : 'Confirm delivery of the service to the client. Leave any completion remarks below.'}
            </p>
            <form onSubmit={handleCompleteSubmit}>
              <div className="adv-modal__field">
                <label className="adv-modal__label">
                  {booking.category === 'product' ? 'Fulfillment Notes / Tracking (optional)' : 'Completion Notes (optional)'}
                </label>
                <textarea rows="4" placeholder={booking.category === 'product' ? "E.g., Shipped via USPS, Tracking number 12345" : "Summarize the work completed..."} value={completionNotes} onChange={(e) => setCompletionNotes(e.target.value)} className="adv-modal__textarea" />
              </div>
              <div className="adv-modal__actions">
                <button
                  type="button"
                  className="adv-btn-ghost"
                  onClick={() => { setCompleteModalOpen(false); setCompletionNotes(''); }}
                  disabled={isCompleting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="adv-btn-success"
                  disabled={isCompleting}
                  style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}
                >
                  {isCompleting ? (
                    <>
                      <span className="adv-btn-spinner"></span>
                      {booking.category === 'product' ? 'Fulfilling...' : 'Completing...'}
                    </>
                  ) : (
                    booking.category === 'product' ? 'Confirm Fulfillment' : 'Confirm Completion'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Dispute Modal */}
      {disputeModalOpen && (
        <div className="adv-modal-overlay">
          <div className="adv-modal">
            <h2 className="adv-modal__title">Raise a Dispute</h2>
            <p className="adv-modal__desc">Please describe the issue. Our support team will investigate the details.</p>
            <form onSubmit={handleDisputeSubmit}>
              <div className="adv-modal__field">
                <label className="adv-modal__label">Reason for Dispute *</label>
                <CustomDropdown
                  value={disputeReason}
                  onChange={(val) => setDisputeReason(val)}
                  options={DISPUTE_REASON_OPTIONS}
                />
              </div>
              <div className="adv-modal__field">
                <label className="adv-modal__label">Description *</label>
                <textarea required rows="4" maxLength={1000} placeholder="Provide a clear description of the issue..." value={disputeDescription} onChange={(e) => setDisputeDescription(e.target.value)} className="adv-modal__textarea" />
                <span className="adv-modal__char-count">{1000 - disputeDescription.length} characters left</span>
              </div>
              <div className="adv-modal__field">
                <label className="adv-modal__label">Evidence Photos (optional)</label>
                <input type="file" multiple accept="image/*" onChange={handleEvidenceFileChange} style={{ display: 'none' }} id="evidence-file-input" />
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <label htmlFor="evidence-file-input" className="adv-btn-outline" style={{ cursor: 'pointer', fontSize: '12px' }}>Select Images</label>
                  {uploading && <span className="adv-uploading-text">Uploading...</span>}
                </div>
                {evidenceUrls.length > 0 && (
                  <div className="adv-evidence-grid" style={{ marginTop: '10px' }}>
                    {evidenceUrls.map((url, i) => (
                      <div key={i} style={{ position: 'relative' }}>
                        <img src={url} alt="Evidence" className="adv-evidence-img" />
                        <button type="button" className="adv-evidence-remove" onClick={() => setEvidenceUrls(prev => prev.filter((_, idx) => idx !== i))}>×</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="adv-modal__actions">
                <button
                  type="button"
                  className="adv-btn-ghost"
                  onClick={() => { setDisputeModalOpen(false); setDisputeDescription(''); setEvidenceUrls([]); }}
                  disabled={isSubmittingDispute || uploading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmittingDispute || uploading}
                  className="adv-btn-destructive"
                  style={{ opacity: (isSubmittingDispute || uploading) ? 0.6 : 1, display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}
                >
                  {isSubmittingDispute ? (
                    <>
                      <span className="adv-btn-spinner"></span>
                      Submitting...
                    </>
                  ) : (
                    "Submit Dispute"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Provider Dispute Response Modal */}
      {providerDisputeModalOpen && (
        <div className="adv-modal-overlay">
          <div className="adv-modal">
            <h2 className="adv-modal__title">Respond to Dispute</h2>
            <p className="adv-modal__desc">Please provide your explanation and upload any evidence or logs to support your case.</p>
            <form onSubmit={handleProviderDisputeSubmit}>
              <div className="adv-modal__field">
                <label className="adv-modal__label">Explanation / Notes *</label>
                <textarea required rows="4" maxLength={1000} placeholder="Explain your perspective..." value={providerExplanationText} onChange={(e) => setProviderExplanationText(e.target.value)} className="adv-modal__textarea" />
                <span className="adv-modal__char-count">{1000 - providerExplanationText.length} characters left</span>
              </div>
              <div className="adv-modal__field">
                <label className="adv-modal__label">Counter-Evidence Photos (optional)</label>
                <input type="file" multiple accept="image/*" onChange={handleProviderEvidenceFileChange} style={{ display: 'none' }} id="provider-evidence-file-input" />
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <label htmlFor="provider-evidence-file-input" className="adv-btn-outline" style={{ cursor: 'pointer', fontSize: '12px' }}>Select Images</label>
                  {uploading && <span className="adv-uploading-text">Uploading...</span>}
                </div>
                {providerEvidenceUrls.length > 0 && (
                  <div className="adv-evidence-grid" style={{ marginTop: '10px' }}>
                    {providerEvidenceUrls.map((url, i) => (
                      <div key={i} style={{ position: 'relative' }}>
                        <img src={url} alt="Provider evidence preview" className="adv-evidence-img" />
                        <button type="button" className="adv-evidence-remove" onClick={() => setProviderEvidenceUrls(prev => prev.filter((_, idx) => idx !== i))}>×</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="adv-modal__actions">
                <button
                  type="button"
                  className="adv-btn-ghost"
                  onClick={() => { setProviderDisputeModalOpen(false); setProviderExplanationText(''); setProviderEvidenceUrls([]); }}
                  disabled={isSubmittingProviderDispute || uploading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmittingProviderDispute || uploading}
                  className="adv-btn-primary"
                  style={{ opacity: (isSubmittingProviderDispute || uploading) ? 0.6 : 1, display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}
                >
                  {isSubmittingProviderDispute ? (
                    <>
                      <span className="adv-btn-spinner"></span>
                      Submitting...
                    </>
                  ) : (
                    "Submit Response"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmationModal
        isOpen={showStartConfirm}
        onClose={() => setShowStartConfirm(false)}
        onConfirm={handleConfirmStartService}
        title={booking.category === 'product' ? "Fulfill Order?" : "Start Service?"}
        message={booking.category === 'product' ? "Are you ready to mark this order as in fulfillment? This will notify the customer." : "Are you ready to mark this service as started? This will notify the customer."}
        confirmText={booking.category === 'product' ? "Yes, Fulfill Order" : "Yes, Start Service"}
        cancelText="Cancel"
        isDanger={false}
        isLoading={isStarting}
      />

      <ConfirmationModal
        isOpen={showConfirmCompletionModal}
        onClose={() => setShowConfirmCompletionModal(false)}
        onConfirm={handleConfirmConfirmCompletion}
        title="Confirm Completion?"
        message="Are you sure you want to confirm completion and close this order?"
        confirmText="Yes, Confirm"
        cancelText="Go Back"
        isDanger={false}
        isLoading={isConfirming}
      />

      <LeaveReviewModal
        isOpen={reviewModalOpen}
        onClose={() => setReviewModalOpen(false)}
        bookingId={booking.bookingId}
        onSubmitSuccess={fetchDetailsAndTimeline}
      />
    </div>
  );
}
